import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { pickSmsBody, ProductRuleLike, LegacySmsTemplateDef } from './smsConfig';

export interface TrackingOrder {
  productOrderNumber: string;
  recipientName: string;
  recipientPhone?: string;
  productName?: string;
  quantity?: number;
}

interface Props {
  orders: TrackingOrder[];
  courier: string;
  vendorCode: string;
  productRules: ProductRuleLike[];
  legacyTemplates?: LegacySmsTemplateDef[]; // 하위호환 fallback
  onSuccess?: (msg: string) => void;
}

type Mode = 'text' | 'image' | 'excel';

interface ParsedTracking {
  trackingNumber: string;
  name: string;
}

const COURIER_OPTIONS = ['CJ대한통운', '한진택배', '롯데택배', '로젠택배', '우체국택배', '농협택배', '경동택배'];

function authHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionStorage.getItem('admin-token') || ''}` };
}

function normalizeHeader(s: any): string {
  return String(s ?? '').replace(/\s+/g, '').toLowerCase();
}

// 엑셀 시트에서 '송장번호'와 '이름' 컬럼 자동 감지 후 파싱
function parseExcelTracking(buf: ArrayBuffer): ParsedTracking[] {
  const wb = XLSX.read(buf, { type: 'array' });
  for (const sheetName of wb.SheetNames) {
    const ws = wb.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1, defval: '' });
    if (!rows || rows.length < 2) continue;

    // 헤더 후보 행 찾기 (상단 10행 중 '송장'/'운송장'/'이름'/'받' 등이 있는 첫 행)
    let headerIdx = -1;
    for (let r = 0; r < Math.min(10, rows.length); r++) {
      const row = rows[r] || [];
      const hasKey = row.some(c => /송장|운송장|등록번호|tracking/i.test(String(c)));
      if (hasKey) { headerIdx = r; break; }
    }
    if (headerIdx < 0) continue;

    const headers = (rows[headerIdx] || []).map(normalizeHeader);
    const findCol = (...patterns: RegExp[]) => {
      for (let c = 0; c < headers.length; c++) {
        for (const p of patterns) if (p.test(headers[c])) return c;
      }
      return -1;
    };
    const trackingCol = findCol(/송장번호/, /운송장번호/, /등록번호/, /송장/, /운송장/, /tracking/);
    const nameCol = findCol(/받는분성명|받는분이름|받으시는분|받는사람|수취인명|수령인|수하인|받는이|받는분|받는자|이름|성명/);
    if (trackingCol < 0 || nameCol < 0) continue;

    const result: ParsedTracking[] = [];
    for (let r = headerIdx + 1; r < rows.length; r++) {
      const row = rows[r] || [];
      const tn = String(row[trackingCol] ?? '').trim();
      const nm = String(row[nameCol] ?? '').trim();
      if (!tn || !nm) continue;
      // 송장번호는 숫자+하이픈만 보존
      const digits = tn.replace(/[^\d-]/g, '');
      if (!digits) continue;
      result.push({ trackingNumber: digits, name: nm });
    }
    if (result.length > 0) return result;
  }
  return [];
}

export default function TrackingRegister({ orders, courier, vendorCode, productRules, legacyTemplates, onSuccess }: Props) {
  const [mode, setMode] = useState<Mode>('text');
  const [trackingText, setTrackingText] = useState('');
  const [excelParsed, setExcelParsed] = useState<ParsedTracking[]>([]);
  const [excelFilename, setExcelFilename] = useState('');
  const [excelCourier, setExcelCourier] = useState(courier);
  const [excelDragOver, setExcelDragOver] = useState(false);
  const [excelError, setExcelError] = useState('');
  const excelRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  useEffect(() => { setTrackingText(''); setExcelParsed([]); setExcelFilename(''); setExcelError(''); }, [location.pathname]);
  useEffect(() => { setExcelCourier(courier); }, [courier]);

  // 현재 모드의 입력으로 parsed tracking 선정
  const parsedTracking: ParsedTracking[] = useMemo(() => {
    if (mode === 'excel') return excelParsed;
    // text 모드
    return trackingText
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(line => {
        const nameMatch = line.match(/[가-힣]+/);
        const trackingMatch = line.match(/\d+(?:-\d+)*/);
        return nameMatch && trackingMatch ? { trackingNumber: trackingMatch[0], name: nameMatch[0] } : null;
      })
      .filter((x): x is ParsedTracking => x !== null);
  }, [mode, trackingText, excelParsed]);

  const activeCourier = mode === 'excel' ? excelCourier : courier;

  const shippingPairs = useMemo(() => {
    const trackingByName = new Map<string, string[]>();
    parsedTracking.forEach(t => {
      const list = trackingByName.get(t.name) || [];
      list.push(t.trackingNumber);
      trackingByName.set(t.name, list);
    });

    const pairs: { productOrderNumber: string; trackingNumber: string; name: string; phone: string; productName: string; quantity: number; matched: boolean }[] = [];
    const cursors = new Map<string, number>();

    orders.forEach(order => {
      const trackings = trackingByName.get(order.recipientName) || [];
      if (trackings.length === 0) return;
      let trackingNumber: string;
      if (trackings.length === 1) {
        trackingNumber = trackings[0];
      } else {
        const idx = cursors.get(order.recipientName) || 0;
        trackingNumber = trackings[Math.min(idx, trackings.length - 1)];
        cursors.set(order.recipientName, idx + 1);
      }
      pairs.push({
        productOrderNumber: order.productOrderNumber,
        trackingNumber,
        name: order.recipientName,
        phone: order.recipientPhone || '',
        productName: order.productName || '',
        quantity: order.quantity || 1,
        matched: !!order.productOrderNumber,
      });
    });

    parsedTracking.forEach(t => {
      const hasOrder = orders.some(o => o.recipientName === t.name);
      if (!hasOrder) {
        pairs.push({ productOrderNumber: '', trackingNumber: t.trackingNumber, name: t.name, phone: '', productName: '', quantity: 1, matched: false });
      }
    });

    return pairs;
  }, [orders, parsedTracking]);

  const trackingMatched = shippingPairs.filter(p => p.matched).length;
  const trackingTotal = shippingPairs.length;

  const downloadShippingExcel = async () => {
    const pairs = shippingPairs.filter(p => p.matched).map(p => ({
      productOrderNumber: p.productOrderNumber,
      trackingNumber: p.trackingNumber,
      recipientName: p.name,
      recipientPhone: p.phone,
      productName: p.productName,
      quantity: p.quantity,
    }));
    if (pairs.length === 0) { alert('매칭된 항목이 없습니다'); return; }

    const res = await fetch('/api/vendor/shipping-export', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ pairs, courier: activeCourier, vendorCode }),
    });
    if (!res.ok) { alert('다운로드 실패'); return; }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const today = new Date();
    const dateStr = `${today.getFullYear()}.${String(today.getMonth() + 1).padStart(2, '0')}.${String(today.getDate()).padStart(2, '0')}`;
    a.download = `${dateStr}_택배발송_템플릿.xlsx`;
    a.click();
    URL.revokeObjectURL(a.href);
    onSuccess?.('택배발송 템플릿이 다운로드되었습니다');
  };

  const handleExcelFile = async (file: File) => {
    setExcelFilename(file.name);
    setExcelError('');
    try {
      const buf = await file.arrayBuffer();
      const parsed = parseExcelTracking(buf);
      if (parsed.length === 0) {
        setExcelError('엑셀에서 송장번호/받는분 컬럼을 찾지 못했습니다. 헤더에 "송장번호", "받는분" 같은 컬럼명이 있어야 합니다.');
        setExcelParsed([]);
        return;
      }
      setExcelParsed(parsed);
    } catch (e: any) {
      setExcelError('엑셀 파일 읽기 실패: ' + (e?.message || e));
      setExcelParsed([]);
    }
  };

  const modes: { key: Mode; label: string }[] = [
    { key: 'text', label: '문자 텍스트' },
    { key: 'image', label: '운송장 이미지' },
    { key: 'excel', label: '엑셀' },
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6 p-6">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">운송장 등록 (택배발송 템플릿 생성)</h3>
          <p className="text-xs text-gray-400 mt-1">{mode === 'excel' ? excelCourier : courier}</p>
        </div>
        {(mode === 'text' || mode === 'excel') && trackingTotal > 0 && (
          <div className="text-xs text-gray-500">
            매칭: <strong className={trackingMatched === trackingTotal ? 'text-gray-800' : 'text-orange-500'}>{trackingMatched}</strong> / {trackingTotal}
          </div>
        )}
      </div>

      {/* 모드 탭 */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {modes.map(m => (
          <button
            key={m.key}
            onClick={() => setMode(m.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              mode === m.key ? 'text-jeju-brown border-jeju-brown' : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >{m.label}</button>
        ))}
      </div>

      {mode === 'text' && (
        <>
          <p className="text-xs text-gray-400 mb-2">각 줄: <span className="font-mono">송장번호 이름</span> (예: <span className="font-mono">263664972244 박복순</span>)</p>
          <textarea
            value={trackingText}
            onChange={e => setTrackingText(e.target.value)}
            placeholder="263664972244 박복순&#10;263664972255 유옥연"
            className="w-full h-40 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-jeju-brown resize-none"
          />
        </>
      )}

      {mode === 'image' && (
        <div className="py-10 px-6 text-sm text-gray-500 leading-relaxed bg-gray-50 rounded-lg">
          <p className="font-medium text-gray-700 mb-2">운송장 이미지는 Claude Code로 텍스트 변환 후 등록하세요</p>
          <ol className="list-decimal list-inside space-y-1 text-gray-500">
            <li>Claude Code 대화창에 운송장 라벨 이미지를 첨부합니다.</li>
            <li>이미지에서 <span className="font-mono bg-white px-1.5 py-0.5 rounded border border-gray-200">송장번호 이름</span> 형식으로 정리를 요청합니다.</li>
            <li>결과 텍스트를 복사해 왼쪽 <strong>문자 텍스트</strong> 탭에 붙여넣으세요.</li>
          </ol>
          <p className="text-xs text-gray-400 mt-3">추후 이미지 직접 업로드 기능이 추가될 수 있습니다.</p>
        </div>
      )}

      {mode === 'excel' && (
        <>
          <div className="flex items-end gap-4 mb-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1 font-medium">택배사</label>
              <select
                value={excelCourier}
                onChange={e => setExcelCourier(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm w-40 focus:outline-none focus:ring-1 focus:ring-jeju-brown"
              >
                {COURIER_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                {!COURIER_OPTIONS.includes(excelCourier) && <option value={excelCourier}>{excelCourier}</option>}
              </select>
            </div>
            {excelFilename && <div className="text-xs text-gray-500 pb-2">업로드: <span className="text-gray-700">{excelFilename}</span></div>}
          </div>
          <div
            onDragOver={e => { e.preventDefault(); setExcelDragOver(true); }}
            onDragLeave={() => setExcelDragOver(false)}
            onDrop={e => { e.preventDefault(); setExcelDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleExcelFile(f); }}
            onClick={() => excelRef.current?.click()}
            className={`flex flex-col items-center justify-center py-10 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
              excelDragOver ? 'border-jeju-brown bg-jeju-brown/5' : 'border-gray-300 hover:border-gray-400'
            }`}
          >
            <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleExcelFile(f); e.target.value = ''; }} />
            <div className="text-3xl mb-2 opacity-30">📦</div>
            <p className="text-sm text-gray-400">택배사 엑셀 파일을 드래그하거나 클릭하여 업로드</p>
            <p className="text-xs text-gray-300 mt-1">송장번호·받는분 컬럼을 자동 감지합니다</p>
          </div>
          {excelError && <p className="mt-2 text-xs text-red-500">{excelError}</p>}
          {excelParsed.length > 0 && <p className="mt-2 text-xs text-gray-500">엑셀에서 {excelParsed.length}건 감지됨</p>}
        </>
      )}

      {(mode === 'text' || mode === 'excel') && shippingPairs.length > 0 && (
        <div className="mt-3 max-h-48 overflow-auto border border-gray-100 rounded-lg">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left px-3 py-2 text-gray-500 font-medium">이름</th>
                <th className="text-left px-3 py-2 text-gray-500 font-medium">연락처</th>
                <th className="text-left px-3 py-2 text-gray-500 font-medium">송장번호</th>
                <th className="text-left px-3 py-2 text-gray-500 font-medium">상품주문번호</th>
              </tr>
            </thead>
            <tbody>
              {shippingPairs.map((p, i) => (
                <tr key={i} className={p.matched ? '' : 'bg-orange-50'}>
                  <td className="px-3 py-1.5 text-gray-800">{p.name}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {p.phone ? (() => {
                      const body = pickSmsBody(vendorCode, p.productName, productRules, legacyTemplates);
                      // 문자 본문(템플릿)이 없으면 빈 문자가 발송되지 않도록 링크 비활성화
                      if (!body) {
                        return (
                          <span
                            className="text-gray-400 cursor-not-allowed"
                            title="문자 템플릿이 없어 발송할 수 없습니다. 배송문자 탭에서 기본 템플릿을 설정하거나 이 상품에 템플릿을 매핑하세요."
                          >{p.phone}</span>
                        );
                      }
                      return (
                        <a
                          href={`sms:${p.phone.replace(/-/g, '')}?&body=${encodeURIComponent(body)}`}
                          className="text-jeju-brown hover:underline"
                          title="문자 메시지 보내기 (템플릿 자동 선택)"
                        >{p.phone}</a>
                      );
                    })() : <span className="text-gray-400">-</span>}
                  </td>
                  <td className="px-3 py-1.5 text-gray-600 font-mono">{p.trackingNumber}</td>
                  <td className="px-3 py-1.5 text-gray-600 font-mono">
                    {p.matched ? p.productOrderNumber : <span className="text-orange-500">매칭 실패</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(mode === 'text' || mode === 'excel') && (
        <div className="flex justify-end mt-4">
          <button
            onClick={downloadShippingExcel}
            disabled={trackingMatched === 0}
            className="px-5 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:bg-jeju-brown-dark transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >택배발송 템플릿 다운로드</button>
        </div>
      )}
    </div>
  );
}
